import { useMemo, useState } from "react";
import { Form, FormInput, FormSelect, FormActions } from "../Controls/Form";
import UserData from "../Controls/UserData";
import API from "@src/API";
import { toast } from "react-toastify";
import "../../style/scss/FormActions.scss";

const RELATION_OPTIONS = [
  { Key: 1, Value: "Supervisor (direct)" },
  { Key: 2, Value: "Supervisor (indirect)" },
  { Key: 3, Value: "Coworker" },
];

/**
 * Add employment connection form. Prefer EmploymentConnectionEditModal.
 * @param {Array<{role:number,label:string,name:string,id:number}>} organizationOptions
 */
export default function EmploymentConnectionEdit({
  employmentId,
  organizationOptions = [],
  onClose,
}) {
  const empId = Number.parseInt(employmentId, 10) || 0;
  const [userData] = useState(UserData.UserData() || {});
  const [mode, setMode] = useState("existing"); // existing | invite
  const [busy, setBusy] = useState(false);

  const orgOptions = organizationOptions || [];
  const defaultRole = orgOptions.length === 1 ? orgOptions[0].role : "";

  const [form, setForm] = useState({
    ConnectedPersonID: "",
    FirstName: "",
    LastName: "",
    Email: "",
    RelationType: 3,
    OrganizationRole: defaultRole,
  });

  const connections = useMemo(() => {
    return (userData.connections || []).filter((c) => {
      const status = String(c.Status ?? c.status ?? "");
      return status.toLowerCase() !== "deleted";
    });
  }, [userData.connections]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  async function onSaveAsync() {
    if (busy || !empId) return;

    const organizationRole = Number(form.OrganizationRole) || 0;
    if (orgOptions.length === 0) {
      toast.error("Save organizations on this employment first");
      return;
    }
    if (orgOptions.length > 1 && !(organizationRole >= 1 && organizationRole <= 3)) {
      toast.error("Select the joint organization");
      return;
    }

    setBusy(true);
    try {
      const payload =
        mode === "existing"
          ? {
              ConnectedPersonID: Number(form.ConnectedPersonID) || 0,
              RelationType: Number(form.RelationType) || 3,
              OrganizationRole: organizationRole || orgOptions[0]?.role,
            }
          : {
              FirstName: (form.FirstName || "").trim(),
              LastName: (form.LastName || "").trim(),
              Email: (form.Email || "").trim(),
              RelationType: Number(form.RelationType) || 3,
              OrganizationRole: organizationRole || orgOptions[0]?.role,
            };

      if (mode === "existing" && !(payload.ConnectedPersonID > 0)) {
        toast.error("Select a connection");
        return;
      }
      if (mode === "invite" && (!payload.FirstName || !payload.LastName || !payload.Email)) {
        toast.error("First name, last name, and email are required");
        return;
      }

      const resp = await API.postAsync(`/PersonEmployment/${empId}/Connections`, payload);
      if (resp?.ok) {
        toast.success("Connection added");
        onClose?.(resp.jsonData);
      } else {
        toast.error(resp?.jsonData?.message || resp?.jsonData?.response || "Could not add connection");
      }
    } finally {
      setBusy(false);
    }
  }

  const showOrgPicker = orgOptions.length > 1;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={form} onUpdate={setForm}>
        <p className="fieldHint">
          Link someone you worked with on this job. If they are not on CareerMaster yet, invite them
          by email — they can verify after they join.
        </p>

        <div className="field form-group">
          <label className="form-label">Person</label>
          <div className="employment-connection-mode">
            <label>
              <input
                type="radio"
                name="emp-conn-mode"
                checked={mode === "existing"}
                onChange={() => setMode("existing")}
              />{" "}
              Existing connection
            </label>
            <label>
              <input
                type="radio"
                name="emp-conn-mode"
                checked={mode === "invite"}
                onChange={() => setMode("invite")}
              />{" "}
              Invite by email
            </label>
          </div>
        </div>

        {mode === "existing" ? (
          <div className="field form-group">
            <label className="form-label" htmlFor="emp-conn-person">
              Connection
            </label>
            <select
              id="emp-conn-person"
              className="form-input"
              data-testid="employment-connection-person"
              value={form.ConnectedPersonID}
              onChange={(e) => update({ ConnectedPersonID: e.target.value })}
            >
              <option value="">Select a connection…</option>
              {connections.map((c) => {
                const pid = c.PersonID ?? c.personID;
                return (
                  <option key={pid} value={pid}>
                    {(c.FirstName ?? c.firstName) + " " + (c.LastName ?? c.lastName)}
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          <>
            <FormInput field="FirstName" label="First name" />
            <FormInput field="LastName" label="Last name" />
            <FormInput field="Email" label="Email" />
          </>
        )}

        <FormSelect
          field="RelationType"
          label="Relationship"
          options={RELATION_OPTIONS}
          valueProp="Key"
          labelProp="Value"
        />

        {showOrgPicker ? (
          <div className="field form-group">
            <label className="form-label" htmlFor="emp-conn-org">
              Joint organization
            </label>
            <select
              id="emp-conn-org"
              className="form-input"
              data-testid="employment-connection-organization"
              value={form.OrganizationRole}
              onChange={(e) => update({ OrganizationRole: Number(e.target.value) || "" })}
              required
            >
              <option value="">Select organization…</option>
              {orgOptions.map((o) => (
                <option key={o.role} value={o.role}>
                  {o.label}: {o.name}
                </option>
              ))}
            </select>
            <p className="fieldHint">Where you worked together among this employment’s organizations.</p>
          </div>
        ) : orgOptions.length === 1 ? (
          <p className="fieldHint" data-testid="employment-connection-org-default">
            Joint organization: {orgOptions[0].label} — {orgOptions[0].name}
          </p>
        ) : (
          <p className="fieldHint">Add an Employer on the Organizations tab before linking people.</p>
        )}

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onSaveAsync={onSaveAsync}
          onCancelAsync={async () => onClose?.()}
        />
      </Form>
    </div>
  );
}
